// Read-only view of the signed-in user's perk wallet (free score tokens,
// featured submission passes, custom-upload passes). Updated by server
// functions only — clients can never write here.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PerkWallet {
  scoreTokens: number;
  featuredPasses: number;
  customFeaturedPasses: number;
  tokensExpireAt: string | null;
  tokensActive: boolean;
}

const EMPTY: PerkWallet = {
  scoreTokens: 0,
  featuredPasses: 0,
  customFeaturedPasses: 0,
  tokensExpireAt: null,
  tokensActive: false,
};

export function useUserPerks() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState<PerkWallet>(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setWallet(EMPTY);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("user_perk_wallet")
      .select("score_tokens,featured_passes,custom_featured_passes,tokens_expire_at")
      .eq("user_id", user.id)
      .maybeSingle();
    const exp = data?.tokens_expire_at ?? null;
    const expActive = exp ? new Date(exp).getTime() > Date.now() : false;
    setWallet({
      scoreTokens: data?.score_tokens ?? 0,
      featuredPasses: data?.featured_passes ?? 0,
      customFeaturedPasses: data?.custom_featured_passes ?? 0,
      tokensExpireAt: exp,
      tokensActive: expActive && (data?.score_tokens ?? 0) > 0,
    });
    setLoading(false);
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`perks-${user.id}-${crypto.randomUUID()}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_perk_wallet", filter: `user_id=eq.${user.id}` },
        () => { void refresh(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, refresh]);

  return { wallet, loading, refresh };
}
